import { create } from 'zustand'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, doc, onSnapshot, query, where, orderBy } from 'firebase/firestore'
import { checkoutAppOrigin, openStripeCheckout } from '@/utils/stripeCheckout'
import { db } from '@/config/firebase'
import { derived } from '@/lib/storeHelpers'

// Fallback pass pricing configuration (used if Firestore fails)
const FALLBACK_PASS_PRICES: Record<string, any> = {
  '1-game': {
    type: '1-game',
    name: 'Single Game',
    price: 18,
    games: 1,
    description: 'Pay as you go',
    order: 1,
    isActive: false
  },
  '5-game': {
    type: '5-game',
    name: '5 Game Pass',
    price: 85,
    games: 5,
    description: 'Save $5 compared to single games',
    order: 2,
    isActive: true
  },
  '10-game': {
    type: '10-game',
    name: '10 Game Pass',
    price: 160,
    games: 10,
    description: 'Best value - Save $20',
    order: 3,
    isActive: true
  },
  'full-season': {
    type: 'full-season',
    name: 'Full Season',
    price: 600,
    games: 'Unlimited',
    description: 'Unlimited games all season',
    order: 4,
    isActive: false
  },
  'spring26-1nw-8': {
    type: 'spring26-1nw-8',
    name: 'Spring Pass – April & May · 1 night / week',
    price: 140,
    games: 8,
    description: '8 sessions — one night per week',
    order: 5,
    isActive: false
  },
  'spring26-2nw-16': {
    type: 'spring26-2nw-16',
    name: 'Spring Pass – April & May · 2 nights / week',
    price: 270,
    games: 16,
    description: '16 sessions — two nights per week',
    order: 6,
    isActive: false
  },
  'spring26-3nw-24': {
    type: 'spring26-3nw-24',
    name: 'Spring Pass – April & May · 3 nights / week',
    price: 390,
    games: 24,
    description: '24 sessions — three nights per week',
    order: 7,
    isActive: false
  },
  'spring26-god-32': {
    type: 'spring26-god-32',
    name: 'Spring Pass – April & May · God mode (4× / week)',
    price: 500,
    games: 32,
    description: 'Up to 32 sessions — max flexibility',
    order: 8,
    isActive: false
  }
}

export interface PaymentResult {
  success: boolean
  error?: string | null
  sessionId?: string
  url?: string
}

export interface PaymentState {
  loading: boolean
  error: string | null
  currentCheckoutSession: string | null
  passConfigsLoaded: boolean
  passConfigsError: string | null
  /** Dynamic pass configurations from Firestore (internal in the legacy store). */
  passConfigs: Record<string, any>
  // derived
  PASS_PRICES: Record<string, any>
  SINGLE_GAME_PRICE: number
  // actions
  initiateCheckout: (passType: string, cityId: string) => Promise<PaymentResult>
  initiateSingleGameCheckout: (gameId: string, isWaitlist: boolean, cityId: string, position?: string) => Promise<PaymentResult>
  capturePayment: (paymentIntentId: string) => Promise<PaymentResult>
  cancelPaymentAuthorization: (paymentIntentId: string) => Promise<PaymentResult>
  getFormattedPrice: (passType: string) => string
  getAvailablePasses: () => any[]
  clearError: () => void
  subscribeToPassConfigs: () => void
  unsubscribeFromPassConfigs: () => void
}

let passConfigsUnsubscribe: (() => void) | null = null
let singleGameUnsubscribe: (() => void) | null = null

export const usePaymentStore = create<PaymentState>()(
  derived<PaymentState>((s) => {
    const singleGamePass = s.passConfigs['1-game']
    const n = Number(singleGamePass?.price)
    return {
      // Get the current pass prices (reactive)
      PASS_PRICES: Object.keys(s.passConfigs).length > 0 ? s.passConfigs : FALLBACK_PASS_PRICES,
      // Get single game price (reactive)
      SINGLE_GAME_PRICE: Number.isFinite(n) && n > 0 ? n : 18
    }
  })((set, get) => {
    const mergePassConfigs = (incoming) => {
      const next = { ...incoming }
      const single = get().passConfigs['1-game']
      if (single) {
        next['1-game'] = single
      }
      set({ passConfigs: next })
    }

    const subscribeToSingleGamePrice = () => {
      if (singleGameUnsubscribe) return
      singleGameUnsubscribe = onSnapshot(
        doc(db, 'passConfigs', '1-game'),
        (snap) => {
          if (!snap.exists()) return
          set({
            passConfigs: {
              ...get().passConfigs,
              '1-game': { type: '1-game', ...snap.data() }
            }
          })
        },
        (err) => {
          console.error('Error subscribing to 1-game pass config:', err)
        }
      )
    }

    return {
      loading: false,
      error: null,
      currentCheckoutSession: null,
      passConfigs: {},
      passConfigsLoaded: false,
      passConfigsError: null,
      PASS_PRICES: FALLBACK_PASS_PRICES,
      SINGLE_GAME_PRICE: 18,

      /**
       * Subscribe to pass configurations from Firestore
       * Called once when the store is initialized
       */
      subscribeToPassConfigs: () => {
        if (passConfigsUnsubscribe) {
          return // Already subscribed
        }

        subscribeToSingleGamePrice()

        try {
          const q = query(
            collection(db, 'passConfigs'),
            where('isActive', '==', true),
            orderBy('order', 'asc')
          )

          passConfigsUnsubscribe = onSnapshot(q, (snapshot) => {
            const configs = {}
            snapshot.forEach((docSnap) => {
              const data = docSnap.data()
              configs[docSnap.id] = {
                type: docSnap.id,
                ...data
              }
            })

            if (Object.keys(configs).length > 0) {
              mergePassConfigs(configs)
              set({ passConfigsLoaded: true, passConfigsError: null })
            } else {
              console.warn('No pass configs in Firestore, using fallback')
              mergePassConfigs(FALLBACK_PASS_PRICES)
              set({ passConfigsLoaded: true })
            }
          }, (err) => {
            console.error('Error subscribing to pass configs:', err)
            set({ passConfigsError: err.message })
            mergePassConfigs(FALLBACK_PASS_PRICES)
            set({ passConfigsLoaded: true })
          })
        } catch (err) {
          console.error('Error setting up pass configs subscription:', err)
          set({ passConfigsError: err.message })
          mergePassConfigs(FALLBACK_PASS_PRICES)
          set({ passConfigsLoaded: true })
        }
      },

      /**
       * Unsubscribe from pass configurations
       */
      unsubscribeFromPassConfigs: () => {
        if (passConfigsUnsubscribe) {
          passConfigsUnsubscribe()
          passConfigsUnsubscribe = null
        }
        if (singleGameUnsubscribe) {
          singleGameUnsubscribe()
          singleGameUnsubscribe = null
        }
      },

      /**
       * Initiate checkout for a pass type
       * @param passType - One of the pass types from passConfigs
       * @param cityId - Current city ID for return URL
       */
      initiateCheckout: async (passType, cityId) => {
        const prices = get().PASS_PRICES
        if (!prices[passType]) {
          set({ error: 'Invalid pass type' })
          return { success: false, error: 'Invalid pass type' }
        }

        set({ loading: true, error: null })

        try {
          const baseUrl = checkoutAppOrigin()

          // Build success and cancel URLs
          const successUrl = `${baseUrl}/${cityId}/payment-success?session_id={CHECKOUT_SESSION_ID}`
          const cancelUrl = `${baseUrl}/${cityId}/payment-cancel`

          // On web, use httpsCallable as Firebase JS SDK auth is available
          const functions = getFunctions()
          const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession')

          const result = await createCheckoutSession({
            passType,
            cityId,
            successUrl,
            cancelUrl
          })

          const data = result.data as any
          const sessionId = data.sessionId
          const url = data.url

          set({ currentCheckoutSession: sessionId })

          await openStripeCheckout(url)

          set({ loading: false })
          return { success: true, sessionId, url }
        } catch (err) {
          console.error('Checkout error:', err)
          set({ error: err.message || 'Failed to initiate checkout', loading: false })
          return { success: false, error: get().error }
        }
      },

      /**
       * Initiate checkout for a single game payment
       * @param gameId - The game ID to pay for
       * @param isWaitlist - Whether user is on the waitlist (authorize only)
       * @param cityId - Current city ID for return URL
       */
      initiateSingleGameCheckout: async (gameId, isWaitlist, cityId, position) => {
        if (!gameId) {
          set({ error: 'Game ID is required' })
          return { success: false, error: 'Game ID is required' }
        }

        set({ loading: true, error: null })

        try {
          const baseUrl = checkoutAppOrigin()

          // Success URL goes back to homepage with success param
          const successUrl = `${baseUrl}/${cityId}?payment=success&gameId=${gameId}`
          const cancelUrl = `${baseUrl}/${cityId}?payment=cancelled`

          // On web, use httpsCallable
          const functions = getFunctions()
          const createSingleGameCheckout = httpsCallable(functions, 'createSingleGameCheckout')

          const result = await createSingleGameCheckout({
            gameId,
            isWaitlist,
            cityId,
            position,
            successUrl,
            cancelUrl
          })

          const data = result.data as any
          const sessionId = data.sessionId
          const url = data.url

          set({ currentCheckoutSession: sessionId })

          await openStripeCheckout(url)

          set({ loading: false })
          return { success: true, sessionId, url }
        } catch (err) {
          console.error('Single game checkout error:', err)
          set({ error: err.message || 'Failed to initiate checkout', loading: false })
          return { success: false, error: get().error }
        }
      },

      /**
       * Capture a previously authorized payment (for waitlist players moved to team)
       * @param paymentIntentId - The Stripe Payment Intent ID
       */
      capturePayment: async (paymentIntentId) => {
        if (!paymentIntentId) {
          return { success: false, error: 'Payment Intent ID is required' }
        }

        set({ loading: true, error: null })

        try {
          const functions = getFunctions()
          const capturePaymentFn = httpsCallable(functions, 'capturePayment')
          const result = await capturePaymentFn({ paymentIntentId })

          set({ loading: false })
          return { success: (result.data as any).success }
        } catch (err) {
          console.error('Capture payment error:', err)
          set({ error: err.message || 'Failed to capture payment', loading: false })
          return { success: false, error: get().error }
        }
      },

      /**
       * Cancel a previously authorized payment
       * @param paymentIntentId - The Stripe Payment Intent ID
       */
      cancelPaymentAuthorization: async (paymentIntentId) => {
        if (!paymentIntentId) {
          return { success: false, error: 'Payment Intent ID is required' }
        }

        set({ loading: true, error: null })

        try {
          const functions = getFunctions()
          const cancelPaymentFn = httpsCallable(functions, 'cancelPaymentAuthorization')
          const result = await cancelPaymentFn({ paymentIntentId })

          set({ loading: false })
          return { success: (result.data as any).success }
        } catch (err) {
          console.error('Cancel payment authorization error:', err)
          set({ error: err.message || 'Failed to cancel payment', loading: false })
          return { success: false, error: get().error }
        }
      },

      /**
       * Get formatted price for display
       */
      getFormattedPrice: (passType) => {
        const prices = get().PASS_PRICES
        const pass = prices[passType]
        if (!pass) return '$0'
        return `$${pass.price}`
      },

      /**
       * Get all available passes for purchase (sorted by order)
       */
      getAvailablePasses: () => {
        const prices = get().PASS_PRICES
        return Object.values(prices)
          .filter(pass => pass.isActive !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      },

      /**
       * Clear any error state
       */
      clearError: () => {
        set({ error: null })
      }
    }
  })
)

// Initialize subscription on store creation
usePaymentStore.getState().subscribeToPassConfigs()
